import api, { FetchError } from './api';

/**
 * PUT wrapper for the form-submit hooks (CHLOE-MOBILE-APP-11, the CD-136/149
 * family): the api layer already aborts requests after `fetchTimeoutMs` and
 * rejects with TimeoutError, but the upsert hooks awaited `.response` bare —
 * the rejection escaped as an unhandled rejection, no error state was ever
 * set, and the submit spinner spun forever. This wrapper catches every
 * rejection and guards JSON parsing on both the success and error paths (an
 * ALB 502/504 HTML body used to throw out of the hook the same way).
 *
 * Retry policy: only infra-shaped response failures retry (5xx bodies,
 * unparseable bodies). Timeouts and network failures do NOT retry — the
 * request may have committed server-side, and automatically replaying a
 * create risks the CD-75 duplicate family. The data sources' existing
 * resubmit flow stays in charge of user-driven retries.
 *
 * Never rejects — always resolves `{ json }` or `{ error }`.
 */

const MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1000;

export interface GuardedPutResult<T = any> {
  json?: T;
  error?: FetchError;
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const guardedPut = async <T = any>(
  path: string,
  body: any,
  options?: { accessToken?: string }
): Promise<GuardedPutResult<T>> => {
  let lastError: FetchError = {
    statusCode: 0,
    error: 'RequestFailed',
    message: 'Request failed'
  } as FetchError;

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    if (attempt > 0) await sleep(RETRY_DELAY_MS);

    let result: Response;
    try {
      result = await (options
        ? api.put(path, body, options)
        : api.put(path, body)
      ).response;
    } catch (err: any) {
      // the request may have committed server-side — surface, don't replay
      return {
        error:
          err?.name === 'TimeoutError'
            ? ({
                statusCode: 408,
                error: 'TimeoutError',
                message: 'The request timed out. Please try again.'
              } as FetchError)
            : ({
                statusCode: 0,
                error: 'NetworkError',
                message: err?.message || 'Network request failed'
              } as FetchError)
      };
    }

    if (result.ok) {
      try {
        return { json: (await result.json()) as T };
      } catch {
        // a 2xx with an unparseable body is infra junk — retryable
        lastError = {
          statusCode: result.status,
          error: 'InvalidResponseBody',
          message: result.statusText || 'Invalid response body'
        } as FetchError;
        continue;
      }
    }

    try {
      lastError = (await result.json()) as FetchError;
    } catch {
      // non-JSON error bodies (e.g. ALB 502 HTML pages) are infra errors —
      // preserve the status and never shape them as Unauthorized
      lastError = {
        statusCode: result.status,
        message: result.statusText || 'Request failed'
      } as FetchError;
    }
    // client errors are deterministic — retrying only delays the user
    if (result.status < 500) return { error: lastError };
  }

  return { error: lastError };
};
